import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ArrowRight, HelpCircle, Mail, Phone, MessageCircle, FileText, Clock, CheckCircle } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    inquiry_type: 'General Inquiry',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitSuccess(true);
      setFormData({
        name: '',
        email: '',
        organization: '',
        inquiry_type: 'General Inquiry',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-24">
        {/* Hero Banner */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src="https://images.unsplash.com/photo-1559117725-6588a8acb599?auto=format&fit=crop&w=1920"
              alt="Conscious Guidance - Pathway Forward"
              className="w-full h-full object-cover"
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-blue-900/65 to-indigo-900/70"></div>
          </div>
          
          {/* Content */}
          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl md:text-7xl font-bold mb-8 leading-tight">
                Answers that keep you{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">
                  moving
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed">
                From documentation tips to badge usage guidelines and renewals—common questions, clear answers.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-lg px-10 py-6"
                  onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Contact Us
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <a href="mailto:contact@cness.co">
                  <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white hover:text-slate-900">
                    Let's Talk
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Support Options */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
                How Can We Help You?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  {
                    icon: HelpCircle,
                    title: "General Inquiries",
                    description: "Questions about certification process, requirements, or CNESS programs",
                    contact: "contact@cness.co",
                    response: "24 hours"
                  },
                  {
                    icon: FileText,
                    title: "Technical Support",
                    description: "Help with applications, documentation, or platform issues",
                    contact: "contact@cness.co", 
                    response: "4-8 hours"
                  },
                  {
                    icon: MessageCircle,
                    title: "Partnership Inquiries",
                    description: "Mentor applications, partnership opportunities, and collaborations",
                    contact: "contact@cness.co",
                    response: "48 hours"
                  }
                ].map((option, index) => (
                  <Card key={index} className="group hover:shadow-xl hover:-translate-y-2 transition-all duration-300">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center group-hover:from-amber-500 group-hover:to-amber-600 transition-all duration-300">
                        <option.icon className="h-8 w-8 text-amber-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-4">{option.title}</h3>
                      <p className="text-gray-600 mb-6">{option.description}</p>
                      
                      <div className="space-y-2 mb-6">
                        <div className="flex items-center justify-center text-sm text-gray-500">
                          <Mail className="h-4 w-4 mr-2" />
                          {option.contact}
                        </div>
                        <div className="flex items-center justify-center text-sm text-gray-500">
                          <Clock className="h-4 w-4 mr-2" />
                          Response: {option.response}
                        </div>
                      </div>
                      
                      <a href={`mailto:${option.contact}`}>
                        <Button className="w-full bg-gray-900 hover:bg-gray-800 text-white">
                          Contact Support
                        </Button>
                      </a>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
                Frequently Asked Questions
              </h2>
              
              <div className="space-y-8">
                {[
                  {
                    question: "How long does the certification process take?",
                    answer: "The typical certification process takes 6-12 weeks from application to final certification, depending on your chosen tier and the completeness of your submission."
                  },
                  {
                    question: "What documentation do I need to provide?",
                    answer: "Required documentation varies by tier but generally includes policies, procedures, evidence of implementation, stakeholder feedback, performance metrics, and improvement plans."
                  },
                  {
                    question: "How much does CNESS certification cost?",
                    answer: "Pricing is calibrated to organization size and sector. We offer transparent, fair pricing that covers assessment, verification, and certification assets. Contact us for a custom quote."
                  },
                  {
                    question: "Can I upgrade my certification tier later?",
                    answer: "Yes, organizations can apply for higher tiers as they mature in their conscious business practices. We encourage continuous improvement and growth."
                  },
                  {
                    question: "How do I use my CNESS badge and certification?",
                    answer: "Once certified, you receive a digital badge kit with usage guidelines. You can display your badge on websites, marketing materials, and include it in your directory listing."
                  },
                  {
                    question: "What happens if I don't meet certification requirements?",
                    answer: "If requirements aren't met initially, we provide detailed feedback and improvement recommendations. You can reapply once you've addressed the areas for improvement."
                  }
                ].map((faq, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                    <CardContent className="p-8">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">{faq.question}</h3>
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section id="contact-form" className="py-24 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl font-bold text-gray-900 mb-8">
                  Still Have Questions?
                </h2>
                <p className="text-xl text-gray-600">
                  We're here to help. Reach out with any questions or to schedule a consultation.
                </p>
              </div>
              
              <Card className="shadow-xl">
                <CardContent className="p-12">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                        <input 
                          type="text" 
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input 
                          type="email" 
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="your.email@company.com"
                          required
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Organization</label>
                        <input 
                          type="text" 
                          name="organization"
                          value={formData.organization}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                          placeholder="Your organization name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Inquiry Type</label>
                        <select 
                          name="inquiry_type"
                          value={formData.inquiry_type}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        >
                          <option>General Inquiry</option>
                          <option>Certification Questions</option>
                          <option>Technical Support</option>
                          <option>Partnership Interest</option>
                          <option>Media/Press</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                      <textarea 
                        rows="6" 
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                        placeholder="How can we help you?"
                        required
                      ></textarea>
                    </div>
                    
                    {submitSuccess && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                        <p className="text-green-700">Thank you! Your message has been sent successfully. We'll respond within 24 hours.</p>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <Button 
                        type="submit"
                        disabled={isSubmitting}
                        size="lg" 
                        className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white px-12"
                      >
                        {isSubmitting ? 'Sending...' : 'Send Message'}
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                      <p className="text-sm text-gray-500 mt-4">
                        We'll respond within 24 hours • All communications are confidential
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SupportPage;